'use client';

import { createClient } from '@/lib/supabase/client';

export interface Price {
  id: string;
  product_name: string;
  weight: string;
  purity: string;
  ankauf_netto: number;
  verkauf_netto: number;
  verkauf_brutto: number;
  currency: string;
  category: 'gold' | 'silver';
  display_order: number;
  is_active: boolean;
  last_updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PriceInput {
  product_name: string;
  category: 'gold' | 'silver';
  weight: string;
  purity?: string;
  ankauf_netto: number;
  currency?: string;
  display_order?: number;
  is_active?: boolean;
}

class PriceService {
  private supabase: ReturnType<typeof createClient> | null = null;

  private getClient() {
    if (!this.supabase && typeof window !== 'undefined') {
      this.supabase = createClient();
    }
    return this.supabase;
  }

  async getAll(): Promise<Price[]> {
    const supabase = this.getClient();
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('prices')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching prices:', error);
      throw new Error('Failed to load prices');
    }

    return data || [];
  }

  async getAllPrices(): Promise<Price[]> {
    return this.getAll();
  }

  async getPricesByType(type: 'gold' | 'silver'): Promise<Price[]> {
    const supabase = this.getClient();
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('prices')
      .select('*')
      .eq('category', type)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching prices by type:', error);
      return [];
    }

    return data || [];
  }

  async createPrice(price: PriceInput): Promise<Price | null> {
    const supabase = this.getClient();
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('prices')
      .insert([price])
      .select()
      .single();

    if (error) {
      console.error('Error creating price:', error);
      return null;
    }

    return data;
  }

  async updatePrice(id: string, price: Partial<PriceInput>): Promise<Price | null> {
    const supabase = this.getClient();
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('prices')
      .update(price)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating price:', error);
      return null;
    }

    return data;
  }

  async deletePrice(id: string): Promise<boolean> {
    const supabase = this.getClient();
    if (!supabase) return false;

    const { error } = await supabase
      .from('prices')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting price:', error);
      return false;
    }

    return true;
  }
}

export const priceService = new PriceService();